'use client';
import type { InventoryModalProps } from './data';

import React from 'react';
import { useRouter } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';

import InventoryModalItem from './InventoryModalItem';

import {
  Button,
  Center,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@/components';
import {
  useBasketStore,
  useProductGroupInventories,
  useTranslation,
} from '@/hooks';
import { routes } from '@/statics';
import { productCount } from '@/utils';
import { googleEventAddToCart } from '@/utils/googleAnalyticsEvent';

const InventoryModal: InventoryModalProps = ({ product, isOpen, onClose }) => {
  const { t } = useTranslation('components.inventoryModal');
  const router = useRouter();

  const [count, add] = useBasketStore(
    useShallow((state) => [
      productCount({ products: state.products, code: product.code }),
      state.add,
    ])
  );

  const { data, isLoading } = useProductGroupInventories({
    productGroupCodes: [product.code],
    enabled: isOpen,
  });

  const localPickups =
    data?.[product.code].inventories.filter(
      (inventory) => inventory.is_local_pickup
    ) || [];

  const deliveries =
    data?.[product.code].inventories.filter(
      (inventory) => !inventory.is_local_pickup
    ) || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          pb={{ base: '13px', md: '25px' }}
          px={{ base: '18px', md: '54px' }}
          pt={{ base: '33px', md: '50px' }}
        >
          <Stack direction="column" gap="2">
            <Text
              color="text"
              fontSize={{ base: 'sm', md: 'lg' }}
              fontWeight={700}
            >
              {t('ProductInventoryCheck')}
            </Text>
            <Text
              color="text"
              fontSize={{ base: 'xs', md: 'lg' }}
              fontWeight={400}
              lineHeight="33px"
            >
              {product.title}
            </Text>
          </Stack>
        </ModalHeader>
        <Divider />
        <ModalCloseButton fill="black" />
        <ModalBody
          px={{ base: '18px', md: '54px' }}
          pb={{ base: '30px', md: '37px' }}
          pt={{ base: '30px', md: '53px' }}
        >
          {isLoading && (
            <Center h={40}>
              <Spinner color="gray.700" />
            </Center>
          )}
          {data && (
            <Stack direction="column" gap={{ base: '55px', md: '85px' }}>
              <Stack
                direction={{ base: 'column', md: 'row' }}
                gap={{ base: '29px', md: '55px' }}
              >
                <Stack gap={{ base: '8px', md: '19px' }}>
                  <Text fontSize={{ base: 'sm', md: 'lg' }}>
                    {t('stockOnDelivery')}
                  </Text>
                  <SimpleGrid
                    rowGap={1}
                    columnGap={2}
                    columns={2}
                    w={{ base: 'full', md: '350px' }}
                    bg="yellow.150"
                    py="18px"
                    px={1}
                    borderRadius="sm"
                  >
                    {localPickups.length > 0 ? (
                      localPickups.map((inventory) => (
                        <InventoryModalItem
                          key={inventory.warehouse_code}
                          inventoryItem={{
                            ...inventory,
                            warehouse_name: inventory.warehouse_name.replaceAll(
                              'حضوری',
                              ''
                            ),
                          }}
                        />
                      ))
                    ) : (
                      <InventoryModalItem
                        inventoryItem={{
                          warehouse_code: 0,
                          warehouse_name: t('noStockFound'),
                          inventory_count: 0,
                          is_available: false,
                          is_local_pickup: true,
                        }}
                      />
                    )}
                  </SimpleGrid>
                </Stack>
                <Stack gap={{ base: '8px', md: '19px' }}>
                  <Text fontSize={{ base: 'sm', md: 'lg' }}>
                    {t('StockForShippingMethod')}
                  </Text>
                  <SimpleGrid
                    rowGap={1}
                    columnGap={2}
                    columns={2}
                    w={{ base: 'full', md: '350px' }}
                    bg="blue.100"
                    py="18px"
                    px={1}
                    borderRadius="sm"
                  >
                    {deliveries.length > 0 ? (
                      deliveries.map((inventory) => (
                        <InventoryModalItem
                          key={inventory.warehouse_code}
                          inventoryItem={inventory}
                        />
                      ))
                    ) : (
                      <InventoryModalItem
                        inventoryItem={{
                          warehouse_code: 0,
                          warehouse_name: t('noStockFound'),
                          inventory_count: 0,
                          is_available: false,
                          is_local_pickup: false,
                        }}
                      />
                    )}
                  </SimpleGrid>
                </Stack>
              </Stack>
              <Stack direction="row" gap="4" justify="flex-end">
                <Button
                  onClick={() => {
                    if (count === 0) {
                      add({ product, count: 1 });
                      googleEventAddToCart(product);
                    } else {
                      router.push(routes.checkout.url);
                    }
                  }}
                  width={{ base: '100%', md: 'max-content ' }}
                  colorScheme={count === 0 ? 'primary' : 'secondary'}
                >
                  {count === 0 ? t('addToBasket') : t('showBasket')}
                </Button>
              </Stack>
            </Stack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
export default InventoryModal;
